'use client';

import '@utils/text';
import React, { useMemo, useState, useCallback, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { ParsedUrlQuery } from 'next-runtime/types/querystring';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { useBasketId as getBasketIdFromCache } from '@hooks/cache';
import { ApiError, AuthStates, LoginSignUpViewModel } from '@api/index';
import Header from '@components/ui/headers/HeaderSimple/Simple';
import {
  useSetUserInfo,
  useSetLoyaltyPoints,
  DISCONNECTION_REASON_STORAGE_KEY,
  useSetTokenExpirationDate,
  useClearDispatch,
} from '@contexts/auth/AuthContext';
import { FlexibleDiv } from '@components/formStyles';
import { addRegistrationEventsToGTM } from '@utils/gtm/registration-gtm';
import { callService } from '@api/remote-call';
import {
  SECRET_KEY,
  SSO_AUTH_REDIRECT_KEYS,
  SSO_MAIL_KEY,
  SSO_USER_DATA,
} from '@constants/constants';
import { saveLoginSessionExpirationDate } from '@utils/auth/tokens';
import { useDevice } from '@contexts/device';
import { addVLUserInfo } from '@utils/gtm/login-gtm';
import Typography from '@components/Typography';
import { paramsToObject } from '@components/views/utils';
import { loginSuccessGtm, signUpGtm } from '@utils/gtm/membership-gtm';
import {
  getSafeLocalStorageItem,
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';
import { AuthSyncType, useAuthTabSync } from '@utils/auth/useAuthTabSync';
import { useOnce } from '@utils/gtm/hooks/useOnce';
import { useAppRouter } from '@contexts/router';

import { LoginFormData } from './components/LoginForm';
import {
  DynamicDiv,
  LayoutLogin,
  LeftLogin,
  Register,
  RightLogin,
} from './style';
import { Label } from './components/styled';
import { loginRequest, passwordResetRequest } from './requests';
import { PasswordResetFormData } from './components/PasswordResetModal';
import {
  handleUpdateUserBasketIdLogin,
  isSeturEmail,
  handleCreateDefaultWishList,
  saveTokenInfo,
  getExpirationDate,
} from './utils';
import { SeturLoginData } from './components/Form/SeturForm';

const PasswordResetModal = dynamic(
  () => import('./components/PasswordResetModal')
);
const LoginGate = dynamic(() => import('./components/LoginGate'));
const LoyaltyInfoModal = dynamic(
  () => import('./components/Modal/LoyaltyInfoModal')
);
const Information = dynamic(() => import('./components/Information'));

const SuccessModal = dynamic(
  () => import('@components/ui/modals/SuccessModal')
);

type LoginProps = {
  data: LoginSignUpViewModel;
};

const Login = ({ data }: LoginProps) => {
  const [isRegister, setIsRegister] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState<{
    title?: string;
    message?: string;
    status?: boolean;
  }>();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [isSeturEmployee, setIsSeturEmployee] = useState(true);
  const [email, setEmail] = useState<string>('');
  const [showModal, setShowModal] = useState(false);

  const router = useAppRouter();
  const searchParams = useSearchParams();
  const userInfoDispatch = useSetUserInfo();
  const loyaltyPoitsDispatch = useSetLoyaltyPoints();
  const { isDesktop } = useDevice();
  const tokenExpirationDispatch = useSetTokenExpirationDate();
  const auth = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { broadcastAuthChange } = useAuthTabSync();
  const clearUserInformation = useClearDispatch();

  const clearSession = useCallback(async () => {
    broadcastAuthChange(AuthSyncType.LOGOUT, {
      clearBasket: false,
      clearQueryParams: false,
    });
    await clearUserInformation({ clearQueryParams: false, clearBasket: false });
  }, [broadcastAuthChange, clearUserInformation]);

  useOnce(clearSession);

  const toggleRegister = () => {
    signUpGtm('Giriş Yap - Hemen Üye Ol');
    router.push('/uye-ol/setur-extra');
  };
  const query: ParsedUrlQuery = paramsToObject(searchParams?.entries());

  const redirectUrl = (query.redirectUrl as string) || '/';
  const url = useMemo(() => {
    if (redirectUrl) {
      if (Array.isArray(query.redirectUrl)) {
        const joinedRedirectUrl = query.redirectUrl.join(',');
        if (
          joinedRedirectUrl?.includes('checkinDate') ||
          joinedRedirectUrl?.includes('checkoutDate') ||
          joinedRedirectUrl?.includes('oda')
        ) {
          router.replace('/giris-yap');
        } else {
          return joinedRedirectUrl?.startsWith('/')
            ? joinedRedirectUrl
            : `/${joinedRedirectUrl}`;
        }
      } else {
        return redirectUrl?.startsWith('/') ? redirectUrl : `/${redirectUrl}`;
      }
    }
  }, [query, router, redirectUrl]);

  const togglePasswordResetModal = () =>
    setShowPasswordReset((state) => !state);

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(undefined);
    setIsRegister(false);
  };

  const getUserInfo = async (token: string) => {
    try {
      const response2 = await callService(
        { service: 'UsersService', method: 'getUser', version: 'v3' },
        {
          authorization: token,
          withClubs: true,
        }
      );

      if (response2?.b2CUser) {
        await userInfoDispatch(response2.b2CUser);
      }

      if (response2.b2CUser?.id) {
        setSafeLocalStorageItem('userInfo', JSON.stringify(response2.b2CUser));
        addVLUserInfo(response2?.b2CUser?.userName);
        if (response2.loyaltyUser?.customer?.id) {
          setSafeLocalStorageItem(
            'loyaltyUserInfo',
            JSON.stringify(response2.loyaltyUser)
          );
        }

        const userPoints = await callService(
          {
            service: 'LoyaltiesService',
            method: 'pointsHistoryV2',
            version: 'v3',
          },

          {
            authorization: token || '',
          }
        );

        if (userPoints) {
          setSafeLocalStorageItem('seturPoints', JSON.stringify(userPoints));
          loyaltyPoitsDispatch(userPoints);
        }
      }
      if (response2?.b2CUser) {
        userInfoDispatch(response2.b2CUser);
      }
      const basketId = getBasketIdFromCache();
      await handleUpdateUserBasketIdLogin(basketId, token);
      await handleCreateDefaultWishList(token);

      broadcastAuthChange(AuthSyncType.LOGIN);

      const userExpiredClubs =
        response2?.loyaltyUser?.customer?.customerClubSegments.filter(
          (club) => club.showPopup
        );

      if (userExpiredClubs?.length) {
        const separator = (url || '').includes('?') ? '&' : '?';
        window.open(
          `${process.env.NEXT_PUBLIC_HOST}${url}${separator}club_expired=true`,
          '_self'
        );
      } else if (response2.b2CUser?.id) {
        window.open(`${process.env.NEXT_PUBLIC_HOST}${url}`, '_self');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const [isHideLoader, setIsHideLoader] = useState(false);

  const loginQuery = useMutation(
    async (body: LoginFormData) => {
      setSafeLocalStorageItem(SECRET_KEY, JSON.stringify(body));
      const data = await loginRequest(body);
      const token = data.tokenType + ' ' + data.token;
      saveLoginSessionExpirationDate();
      saveTokenInfo(data);

      const tokenExpirationDate = getExpirationDate(data.expires);

      tokenExpirationDispatch(tokenExpirationDate);
      addRegistrationEventsToGTM('Sign In', 'Step 2');
      getUserInfo(token);
      return data;
    },

    {
      onSuccess: (data) => {
        if (data.token) {
          loginSuccessGtm();
        }
        if (data?.states[0] === AuthStates.LOYALTY_NOT_FOUND) {
          setShowModal(true);
          setIsHideLoader(true);
        }
      },
    }
  );

  const passwordResetQuery = useMutation(
    async (body: Parameters<typeof passwordResetRequest>[0]) => {
      await passwordResetRequest(body);
      togglePasswordResetModal();
      setShowSuccessModal({
        status: true,
        title: 'E-posta Gönderildi.',
        message:
          'Parola sıfırlama linki gönderildi. Gönderdiğimiz linke tıklayarak şifrenizi yenileyebilirsiniz.',
      });
    }
  );

  const handleLoginSubmit = async (form: LoginFormData) => {
    try {
      if (!executeRecaptcha) return;
      const token = await executeRecaptcha('login');
      await loginQuery.mutateAsync({
        email: form.email,
        password: form.password,
        token,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handlePasswordResetSubmit = async (form: PasswordResetFormData) => {
    try {
      if (!executeRecaptcha) return;
      const token = await executeRecaptcha('passwordReset');

      await passwordResetQuery.mutateAsync({
        email: form.email,
        token,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const { mutate: handleCheckLoyaltyUser } = useMutation(
    (email: string) =>
      callService(
        { service: 'LoyaltiesService', method: 'check', version: 'v3' },
        {
          body: {
            email: email,
            userId: 0,
          },
        }
      ),
    {
      onSuccess: () => {
        removeSafeLocalStorageItem(SSO_MAIL_KEY);
        auth.signinRedirect();
      },
      onError: (error) => {
        const errorMessage = (error as ApiError)?.body?.errors[0]?.detail;
        if (errorMessage === 'Bu bilgilere ait loyalty üyeliği bulunamadı.') {
          window.open('/uye-girisi/setur-extra', '_self');
        }
      },
    }
  );

  const {
    mutate: handleCheckSsoUser,
    error: ssoUserErrorMessage,
    data: ssoUserData,
  } = useMutation(
    (ssoMail: string) =>
      callService(
        {
          service: 'AuthService',
          method: 'checkSsoUser',
          version: 'v3',
        },
        {
          email: ssoMail,
        }
      ),
    {
      onSuccess: (_, mail) => {
        setSafeLocalStorageItem(
          SSO_AUTH_REDIRECT_KEYS.LOGIN,
          decodeURIComponent(window.location?.search?.split('=')?.[1] ?? '/') ??
            '/'
        );
        if (mail) {
          handleCheckLoyaltyUser(mail);
        }
      },
      onError: () => {
        removeSafeLocalStorageItem(SSO_MAIL_KEY);
      },
    }
  );

  useEffect(() => {
    if (getSafeLocalStorageItem(DISCONNECTION_REASON_STORAGE_KEY)) {
      removeSafeLocalStorageItem(DISCONNECTION_REASON_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    setSafeLocalStorageItem(SSO_USER_DATA, JSON.stringify(ssoUserData));
  }, [ssoUserData]);

  useEffect(() => {
    if (!isRegister) {
      addRegistrationEventsToGTM('Sign In', 'Step 1');
    } else {
      addRegistrationEventsToGTM('Sign Up', 'Step 1');
    }
  }, [isRegister]);

  const handleSeturLogin = (data: SeturLoginData) => {
    if (isSeturEmail(data.seturEmail)) {
      handleCheckSsoUser(data.seturEmail);
      setSafeLocalStorageItem(SSO_MAIL_KEY, data.seturEmail);
    }
    setEmail(data.seturEmail);
    setIsSeturEmployee(isSeturEmail(data.seturEmail));
  };

  // Parsing the err. msgs. that we are getting from the backend
  const ssoError = (ssoUserErrorMessage as ApiError)?.body?.errors[0]?.detail;
  const loginErrors = (loginQuery.error as ApiError)?.body?.errors;
  const passwordResetErrors = (passwordResetQuery.error as ApiError)?.body
    ?.errors;

  return (
    <>
      <Header align='center' />
      <LayoutLogin>
        <DynamicDiv>
          <LeftLogin>
            <LoginGate
              ssoMailError={ssoError}
              loginGateEmail={email}
              isSeturEmployee={isSeturEmployee}
              onSubmitEmail={handleSeturLogin}
              errorMessages={loginErrors}
              onSubmit={handleLoginSubmit}
              onPasswordReset={togglePasswordResetModal}
              isPostLoginLoading={loginQuery.isLoading}
              isSuccess={loginQuery.isSuccess}
              isHideLoader={isHideLoader}
            />
            <Register>
              <Typography color='D300' variant='caption2' weight='Regular'>
                {isRegister ? 'Zaten üye misiniz?' : 'Üye değil misiniz?'}
              </Typography>
              <FlexibleDiv left={7}>
                <Label onClick={toggleRegister}>
                  <Typography color='D400' variant='caption2' weight='Bold'>
                    {isRegister ? 'Giriş Yapın' : 'Üye Olun'}
                  </Typography>
                </Label>
              </FlexibleDiv>
            </Register>
          </LeftLogin>
          {isDesktop ? (
            <RightLogin>
              <Information
                image={data?.images?.[0]}
                title={data?.boldTitle}
                contents={data?.textItems}
              />
            </RightLogin>
          ) : null}
        </DynamicDiv>
      </LayoutLogin>
      {showSuccessModal?.status ? (
        <SuccessModal
          title={showSuccessModal.title}
          message={showSuccessModal.message}
          onCancel={handleCloseSuccessModal}
        />
      ) : null}
      {showPasswordReset ? (
        <PasswordResetModal
          onCancel={togglePasswordResetModal}
          onSubmit={handlePasswordResetSubmit}
          errorMessages={passwordResetErrors}
          isPostResetPasswordLoading={passwordResetQuery.isLoading}
        />
      ) : null}

      {showModal ? (
        <LoyaltyInfoModal
          borderRadius={8}
          onCancel={() => setShowModal(false)}
          onClick={() => {
            const url = redirectUrl
              ? `uye-girisi/setur-extra?redirectUrl=${encodeURIComponent(
                  redirectUrl
                )}`
              : `uye-girisi/setur-extra`;
            window.open(url);
          }}
          title='Setur.com.tr üyeliği seyahatin kazandıran programı Setur Extra’ya dönüşmüştür.'
          message='Bir sonraki formda eksik bilgilerinizi tamamlayarak Setur Extra üyeliğinizi oluşturabilir, kazanmaya hemen başlayabilirsiniz.'
        />
      ) : null}
    </>
  );
};

export default Login;
